import React, { CSSProperties, useEffect, useState } from 'react';
import { useAuth } from 'oidc-react';
import moment from 'moment';
import { LoaderCircle, Package } from 'lucide-react';
import Pagination from '../../components/Pagination/Pagination';
import { usePatchForm } from '../../providers/PatchFormProvider';
import { useTenantSelection } from '../../providers/TenantSelectionProvider';
import Project from '../../common/Utils/project';
import api from '../../components/services/apiService';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSeverityRedirection } from '../../components/services/dashboardService';

/* ------------------------------------------------------------------
   UI Helpers
------------------------------------------------------------------ */
const ProgressBar: React.FC<{ completed: number; total: number }> = ({ completed, total }) => {
  const percentage = total > 0 ? (completed / total) * 100 : 0;
  return (
    <div style={{ width: 60, height: 5, backgroundColor: '#444', borderRadius: 4, overflow: 'hidden' }}>
      <div style={{ width: `${percentage}%`, height: '100%', backgroundColor: '#00FF00' }} />
    </div>
  );
};

const getSquareStyle = (isActive: boolean): CSSProperties => ({
  width: 20,
  height: 20,
  borderRadius: 4,
  border: '2px solid #888',
  backgroundColor: isActive ? '#00FF00' : 'transparent',
  cursor: 'pointer',
  display: 'inline-block',
});

/* ------------------------------------------------------------------
   Main Component
------------------------------------------------------------------ */
interface SoftwareTableProps {
  softwarePatchType: 'OSUpdate' | 'ThirdPartySoftware';
  osUpdateSource?: 'install' | 'uninstall';
  availabilityTab?: boolean;
  setPatchList?: React.Dispatch<React.SetStateAction<any[]>>;
  setSoftwareAvailability?: React.Dispatch<React.SetStateAction<any[]>>;
  onToggleChange?: ((updatedList: any[]) => void) | null;
  onCountChange?: (osCount: number, thirdPartyCount: number) => void;
}

const noopStateSetter: React.Dispatch<React.SetStateAction<any[]>> = () => undefined;

const decodeDurationFromQuery = (raw: string | null): string => {
  if (!raw) return '';
  return raw.replace(/%3E/gi, '>').replace(/%3C/gi, '<').trim();
};

const normalizeRangeToken = (input: string | null | undefined): string => {
  if (!input) return '';

  const compact = decodeDurationFromQuery(input).replace(/\s+/g, '').toLowerCase().replace(/days?/g, '');

  const gtMatch = compact.match(/^>(\d+)$/);
  if (gtMatch) return `>${gtMatch[1]}`;

  const ltMatch = compact.match(/^<(\d+)$/);
  if (ltMatch) return `<${ltMatch[1]}`;

  const rangeMatch = compact.match(/^(\d+)-(\d+)$/);
  if (rangeMatch) return `${rangeMatch[1]}-${rangeMatch[2]}`;

  return compact;
};

const itemsPerPage = 20;

const SoftwareTable: React.FC<SoftwareTableProps> = ({
  softwarePatchType,
  osUpdateSource = 'install',
  availabilityTab = false,
  setPatchList = noopStateSetter,
  setSoftwareAvailability = noopStateSetter,
  onCountChange,
}) => {
  const patchFormContext = usePatchForm();
  const patchFormData = patchFormContext?.patchFormData;
  const { selectedTenantId } = useTenantSelection();
  const auth = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { getSeverityRedirection } = useSeverityRedirection();

  const [loading, setLoading] = useState(false);
  const [softwareList, setSoftwareList] = useState<any[]>([]);
  // Store selected items keyed by PackageIdentifier / KBId. Seed only from items that actually
  // belong to this table's own patch type - RemediationPlanPatchList is shared wizard-wide state,
  // so without this filter a software-package selection from the Third-Party Software step would
  // reappear as pre-selected on the OS-Update step's table (and vice versa), because both screens
  // key their selection off the same PackageIdentifier field. See docs/known-issues equivalent in
  // Defatom.Cloud.PatchCenterAPI's patch-view-windows-os-update-data-issue.md for the bug this caused.
  const [selectedItems, setSelectedItems] = useState<Record<string, any>>(
    (patchFormData as any)?.RemediationPlanPatchList?.reduce((acc: Record<string, any>, item: any) => {
      if (item.PatchType === softwarePatchType) {
        acc[item.PackageIdentifier] = item;
      }
      return acc;
    }, {}) || {},
  );
  const [selectedVersions, setSelectedVersions] = useState<Record<string, string>>({});
  const [currentPage, setCurrentPage] = useState(1);
  const [paginationInfo, setPaginationInfo] = useState<any>(null);

  // ------------------------------------------------------------------
  // Map duration label → representative numeric value (used for API)
  // Handles encoded < and > from URL params
  // ------------------------------------------------------------------
  const mapDurationToRangeValue = (duration: string) => {
    const normalized = normalizeRangeToken(duration);
    return normalized || '0';
  };

  /* ------------------------------------------------------------------
     Fetch Data (per page)
  ------------------------------------------------------------------ */
  useEffect(() => {
    const fetchSoftwareData = async () => {
      try {
        setLoading(true);
        if (!auth?.userData || !selectedTenantId) return;

        // extract view from query param
        const queryParams = new URLSearchParams(location.search);
        const severity = queryParams.get('severity');
        const rawDuration = queryParams.get('duration');
        const duration = decodeDurationFromQuery(rawDuration);
        const groupId = queryParams.get('groupId');

        const tenantId = selectedTenantId;
        // if we have severity,duration and groupId
        if (severity && duration && groupId) {
          const payload: any = {
            severity: severity.toLowerCase(),
            dayRange: mapDurationToRangeValue(duration),
            ...(groupId && groupId !== 'null' ? { tenantGroupID: groupId } : {}),
          };

          const response = await getSeverityRedirection(selectedTenantId as string, payload, softwarePatchType);
          if (response?.data?.data) {
            // Normalize fields and ensure vulnerability scores are numeric
            const normalized =
              softwarePatchType === 'OSUpdate'
                ? response.data.data.map((i: any) => ({
                    ...i,
                    KBId: i.KBId || i.kbId,
                    vulnerabilityDetail:
                      i.vulnerabilityDetail?.map((v: any) => ({
                        cveID: v.cveID || v.cve || 'N/A',
                        score: Number(v.score ?? v.cvss ?? 0),
                      })) ?? [],
                  }))
                : response.data.data.map((i: any) => ({
                    ...i,
                    // for third party, keep versionDetails intact; also expose a flat vulnerabilityDetail for header safety
                    versionDetails: i.versionDetails ?? [],
                  }));

            setSoftwareList(normalized);
            setPaginationInfo(response.data.pagination ?? null);
          } else {
            setSoftwareList([]);
          }
        } else {
          const baseUrl = `${Project.api}patchreposervice/SoftwarePatch/${tenantId}`;
          const osEndpoint = osUpdateSource === 'uninstall' ? 'os-update-installed' : 'os-update-available';
          const endpoint =
            softwarePatchType === 'ThirdPartySoftware'
              ? `${baseUrl}/software-patches-available?pageNumber=${currentPage}&pageSize=${itemsPerPage}`
              : `${baseUrl}/${osEndpoint}?pageNumber=${currentPage}&pageSize=${itemsPerPage}`;

          const payload = {
            FilterType: 'Tenant',
            TenantGroupIDs: [],
            OSFamily: null,
            ExcludeAgentIDs: [],
            IncludeAgentIDs: (patchFormData as any)?.deviceSelection?.selectedDeviceIds,
          };

          const response = await api.post(endpoint, JSON.stringify(payload));
          if (response?.data?.data) {
            // Normalize fields and ensure vulnerability scores are numeric
            const normalized =
              softwarePatchType === 'OSUpdate'
                ? response.data.data.map((i: any) => ({
                    ...i,
                    KBId: i.KBId || i.kbId,
                    vulnerabilityDetail:
                      i.vulnerabilityDetail?.map((v: any) => ({
                        cveID: v.cveID || v.cve || 'N/A',
                        score: Number(v.score ?? v.cvss ?? 0),
                      })) ?? [],
                  }))
                : response.data.data.map((i: any) => ({
                    ...i,
                    // for third party, keep versionDetails intact; also expose a flat vulnerabilityDetail for header safety
                    versionDetails: i.versionDetails ?? [],
                  }));

            setSoftwareList(normalized);
            setPaginationInfo(response.data.pagination ?? null);
          } else {
            setSoftwareList([]);
          }
        }
      } catch (e) {
        console.error('Error fetching software:', e);
        setSoftwareList([]);
      } finally {
        setLoading(false);
      }
    };

    fetchSoftwareData();
  }, [softwarePatchType, osUpdateSource, currentPage, selectedTenantId]);

  /* ------------------------------------------------------------------
     Keep preview / patch list in sync with selections
  ------------------------------------------------------------------ */
  useEffect(() => {
    const patchList = Object.values(selectedItems);

    // ✅ Update patchList only if it actually changed
    setPatchList((prev: any[]) => {
      const prevStr = JSON.stringify(prev ?? []);
      const newStr = JSON.stringify(patchList);
      if (prevStr !== newStr) {
        return patchList;
      }
      return prev;
    });

    if (availabilityTab) {
      const selectedForType = softwareList.filter((item) => {
        const id = softwarePatchType === 'ThirdPartySoftware' ? item.packageIdentifier : item.KBId;
        return !!selectedItems[id];
      });

      // ✅ Update availability only if it actually changed
      setSoftwareAvailability((prev: any[]) => {
        const prevStr = JSON.stringify(prev ?? []);
        const newStr = JSON.stringify(selectedForType);
        if (prevStr !== newStr) {
          return selectedForType;
        }
        return prev;
      });
    }
    // ✅ Don’t include setter functions in deps
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedItems, softwareList, availabilityTab, softwarePatchType]);

  /* ------------------------------------------------------------------
     Helpers to build selected item payload consistently
  ------------------------------------------------------------------ */
  const buildSelectedPayloadForThirdParty = (pkg: any, versionOverride?: string) => {
    const id = pkg.packageIdentifier;
    const version = versionOverride ?? selectedVersions[id] ?? pkg.versionDetails?.[0]?.version ?? '';
    const versionObj = pkg.versionDetails?.find((v: any) => v.version === version) ?? pkg.versionDetails?.[0] ?? {};

    return {
      PackageIdentifier: id,
      PackageName: pkg.packageName,
      TargetVersion: version,
      PatchType: 'ThirdPartySoftware',
      BlobURL: '',
      cvssScores: versionObj?.vulnerabilityDetail?.map((v: any) => v.cvss ?? v.score ?? 0) ?? null,
      cveNumbers: versionObj?.vulnerabilityDetail?.map((v: any) => v.cve ?? v.cveID ?? 'N/A') ?? null,
      releaseDate: moment(versionObj?.dateAvailable).format('DD MMM YYYY'),
      numberOfAgentWithThisPackageIdentifier: versionObj?.numberOfAgentWithThisPackageIdentifier ?? 0,
      numberOfAgentWithThisPackageIdentifierAndVersion: versionObj?.numberOfAgentWithThisPackageIdentifierAndVersion ?? 0,
    };
  };

  const buildSelectedPayloadForOS = (pkg: any) => {
    const id = pkg.KBId;
    return {
      PackageIdentifier: id,
      PackageName: pkg.kbTitle,
      PatchType: 'OSUpdate',
      TargetVersion: '',
      BlobURL: '',
      cvssScores: pkg.vulnerabilityDetail?.map((v: any) => v.cvss ?? v.score ?? 0) ?? [],
      cveNumbers: pkg.vulnerabilityDetail?.map((v: any) => v.cve ?? v.cveID ?? 'N/A') ?? [],
      releaseDate: moment(pkg.dateAvailable).format('DD MMM YYYY'),
      numberOfAgentWithThisPackageIdentifier: pkg.numberOfAgentRequiresThisKBID ?? 0,
      numberOfAgentWithThisPackageIdentifierAndVersion: pkg.numberOfAgentWithThisKBID ?? 0,
    };
  };

  /* ------------------------------------------------------------------
     Helpers: visible list (centralised)
     For ThirdPartySoftware: filter out rows where installedVersion equals latest (if installedVersion exists)
  ------------------------------------------------------------------ */
  const getVisibleList = () => {
    if (softwarePatchType === 'ThirdPartySoftware') {
      return softwareList.filter((item) => {
        const latestVersion = item.versionDetails?.[0]?.version;
        return !(item.installedVersion && latestVersion && item.installedVersion === latestVersion);
      });
    }
    return softwareList;
  };

  /* ------------------------------------------------------------------
     Handlers (with Defatom.Agent rules)
  ------------------------------------------------------------------ */
  const handleToggle = (pkg: any) => {
    const id = softwarePatchType === 'ThirdPartySoftware' ? pkg.packageIdentifier : pkg.KBId;
    const isDefatom = softwarePatchType === 'ThirdPartySoftware' && pkg.packageIdentifier === 'Defatom.Agent';

    setSelectedItems((prev) => {
      const updated = { ...prev };

      // If user clicked on Defatom.Agent
      if (isDefatom) {
        const alreadySelected = !!prev[id];
        if (alreadySelected) {
          // Unselect Defatom.Agent -> allow normal flow afterwards
          delete updated[id];
          return updated;
        }
        // Select only Defatom.Agent (remove all other selections)
        return {
          [id]: buildSelectedPayloadForThirdParty(pkg),
        };
      }

      // If Defatom.Agent is already selected, block selecting any other package
      if (prev['Defatom.Agent']) {
        return prev; // no-op (keep Defatom.Agent selected)
      }

      // Normal toggle behaviour
      if (updated[id]) {
        delete updated[id];
      } else {
        updated[id] = softwarePatchType === 'ThirdPartySoftware' ? buildSelectedPayloadForThirdParty(pkg) : buildSelectedPayloadForOS(pkg);
      }

      return updated;
    });
  };

  const handleVersionChange = (pkgId: string, version: string) => {
    setSelectedVersions((prev) => ({ ...prev, [pkgId]: version }));

    // If this package is already selected, update the selectedItems payload to reflect new version + updated numbers
    setSelectedItems((prev) => {
      if (!prev[pkgId]) return prev;
      // find the pkg in the current softwareList (could be on current page)
      const pkg = softwareList.find((p) => p.packageIdentifier === pkgId) || null;
      if (!pkg) {
        // If package not on current page, still update TargetVersion only
        return {
          ...prev,
          [pkgId]: { ...prev[pkgId], TargetVersion: version },
        };
      }
      const updatedPayload = buildSelectedPayloadForThirdParty(pkg, version);
      return { ...prev, [pkgId]: updatedPayload };
    });
  };

  const handleSelectAll = () => {
    // If Defatom.Agent is already selected, do nothing
    if (selectedItems['Defatom.Agent']) {
      return;
    }

    const visibleList = getVisibleList();

    // Exclude Defatom.Agent from select-all
    const filteredVisibleList = visibleList.filter((item) => (item.packageIdentifier ? item.packageIdentifier !== 'Defatom.Agent' : true));

    const allIds = filteredVisibleList.map((pkg) => (softwarePatchType === 'ThirdPartySoftware' ? pkg.packageIdentifier : pkg.KBId));
    const allSelected = allIds.length > 0 && allIds.every((id) => !!selectedItems[id]);

    setSelectedItems((prev) => {
      const updated = { ...prev };

      if (allSelected) {
        // remove all visible except Defatom.Agent (already excluded)
        allIds.forEach((id) => delete updated[id]);
      } else {
        // add only visible (excluding Defatom.Agent)
        filteredVisibleList.forEach((pkg) => {
          const id = softwarePatchType === 'ThirdPartySoftware' ? pkg.packageIdentifier : pkg.KBId;
          if (!updated[id]) {
            updated[id] =
              softwarePatchType === 'ThirdPartySoftware' ? buildSelectedPayloadForThirdParty(pkg) : buildSelectedPayloadForOS(pkg);
          }
        });
      }

      return updated;
    });
  };

  const handleDevicePatchClick = (pkg: any) => {
    if (softwarePatchType === 'ThirdPartySoftware') {
      navigate(
        `/patchCenter/DeviceNeedingPatch/${selectedTenantId}?type=thirdParty&packageIdentifier=${pkg.packageIdentifier}&packageTitle=${pkg.packageName}&version=${
          selectedVersions[pkg.packageIdentifier] || pkg.versionDetails?.[0]?.version || ''
        }`,
      );
    } else {
      const osType = osUpdateSource === 'uninstall' ? 'osUpdateInstalled' : 'osUpdate';
      navigate(
        `/patchCenter/DeviceNeedingPatch/${selectedTenantId}?type=${osType}&kbid=${pkg.KBId}&title=${pkg.kbTitle}&category=${pkg.category}`,
      );
    }
  };

  // Add this helper function at the component level (after the existing helper functions, before the component)
  const getSelectedCountByType = (selectedItems: Record<string, any>, type: 'OSUpdate' | 'ThirdPartySoftware') => {
    return Object.values(selectedItems).filter((item) => item.PatchType === type).length;
  };

  /* ------------------------------------------------------------------
     Notify parent of count changes
  ------------------------------------------------------------------ */
  useEffect(() => {
    if (onCountChange) {
      const osCount = getSelectedCountByType(selectedItems, 'OSUpdate');
      const thirdPartyCount = getSelectedCountByType(selectedItems, 'ThirdPartySoftware');
      onCountChange(osCount, thirdPartyCount);
    }
  }, [selectedItems, onCountChange]);

  /* ------------------------------------------------------------------
     Render
  ------------------------------------------------------------------ */
  // make header padding match td padding so alignment is correct
  const thStyle: React.CSSProperties = { padding: 20, textAlign: 'left', borderBottom: '2px solid #444' };
  const tdPadding = { padding: 20 };

  // visible list and filtered list used to render and compute select-all state
  const visibleList = getVisibleList();
  const visibleFilteredList = visibleList.filter((item) => (item.packageIdentifier ? item.packageIdentifier !== 'Defatom.Agent' : true));
  const selectAllActive =
    visibleFilteredList.length > 0 &&
    visibleFilteredList.every((pkg) => !!selectedItems[softwarePatchType === 'ThirdPartySoftware' ? pkg.packageIdentifier : pkg.KBId]);
  const isDefatomSelected = !!selectedItems['Defatom.Agent'];

  return (
    <div style={{ padding: 20, backgroundColor: 'rgb(7, 7, 7)', color: '#FFFFFF' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr style={{ borderBottom: '2px solid #444' }}>
            <th style={{ ...thStyle, width: 50, textAlign: 'center' }}>
              <div
                onClick={() => {
                  if (isDefatomSelected) return; // no-op when Defatom.Agent selected
                  handleSelectAll();
                }}
                style={{
                  ...getSquareStyle(selectAllActive),
                  opacity: isDefatomSelected ? 0.4 : 1,
                  cursor: isDefatomSelected ? 'not-allowed' : 'pointer',
                }}
              />
            </th>
            <th style={thStyle}>Patch Title</th>
            <th style={thStyle}>{softwarePatchType === 'ThirdPartySoftware' ? 'Version' : 'Category'}</th>
            <th style={thStyle}>CVE Id</th>
            <th style={thStyle}>CVSS Score</th>
            <th style={thStyle}>Date Available</th>
            <th style={{ ...thStyle, textAlign: 'center' }}>Devices Needing Patch</th>
          </tr>
        </thead>

        <tbody>
          {loading ? (
            <tr>
              <td colSpan={7} style={{ textAlign: 'center', padding: '48px 0' }}>
                <LoaderCircle className="w-8 h-8 mx-auto animate-spin text-gray-400" />
              </td>
            </tr>
          ) : visibleList.length === 0 ? (
            <tr>
              <td colSpan={7} style={{ textAlign: 'center', padding: '48px 0', color: '#999' }}>
                No data available
              </td>
            </tr>
          ) : softwarePatchType === 'ThirdPartySoftware' ? (
            visibleList.map((item: any, i: number) => {
              const versions = item?.versionDetails ?? [];
              const selectedVersion = selectedVersions[item.packageIdentifier] || versions[0]?.version;
              const versionData = versions.find((v: any) => v.version === selectedVersion) ?? {};

              const total = versionData?.numberOfAgentWithThisPackageIdentifier ?? 0;
              const installed = versionData?.numberOfAgentWithThisPackageIdentifierAndVersion ?? 0;
              const remaining = total - installed >= 0 ? total - installed : 0; // avoid negatives

              const isDefatom = item.packageIdentifier === 'Defatom.Agent';
              const disabledBecauseOfDefatom = isDefatomSelected && !isDefatom;

              return (
                <tr key={i} style={{ background: i % 2 === 0 ? '#161617' : '#232222' }}>
                  <td style={{ ...tdPadding, textAlign: 'center' }}>
                    <div
                      onClick={() => {
                        if (disabledBecauseOfDefatom) return;
                        handleToggle(item);
                      }}
                      style={{
                        ...getSquareStyle(!!selectedItems[item.packageIdentifier]),
                        opacity: disabledBecauseOfDefatom ? 0.3 : 1,
                        cursor: disabledBecauseOfDefatom ? 'not-allowed' : 'pointer',
                      }}
                    />
                  </td>

                  <td style={tdPadding} onClick={() => handleDevicePatchClick(item)}>
                    {item.packageName}
                  </td>

                  <td style={tdPadding}>
                    <select
                      value={selectedVersion}
                      onChange={(e) => handleVersionChange(item.packageIdentifier, e.target.value)}
                      disabled={versions.length <= 1 || disabledBecauseOfDefatom}
                      style={{
                        backgroundColor: '#2c2c2c',
                        color: '#ccc',
                        borderRadius: 8,
                        padding: 8,
                        border: 'none',
                        cursor: versions.length <= 1 || disabledBecauseOfDefatom ? 'not-allowed' : 'pointer',
                        opacity: versions.length <= 1 || disabledBecauseOfDefatom ? 0.6 : 1,
                      }}
                    >
                      {versions.map((v: any, idx: number) => (
                        <option key={idx} value={v.version}>
                          {v.version}
                        </option>
                      ))}
                    </select>
                  </td>

                  <td style={tdPadding}>
                    {versionData?.vulnerabilityDetail?.length
                      ? versionData.vulnerabilityDetail.map((v: any, idx: number) => {
                          // safe display of numeric score
                          const score = typeof v.score === 'number' ? v.score : Number(v.cvss ?? v.score ?? NaN);
                          return <p key={idx}>{Number.isFinite(score) ? score.toFixed(2) : '--'}</p>;
                        })
                      : '--'}
                  </td>

                  <td style={tdPadding}>
                    {versionData?.vulnerabilityDetail?.length
                      ? versionData.vulnerabilityDetail.map((v: any, idx: number) => <p key={idx}>{v.cveID || v.cve || 'N/A'}</p>)
                      : '--'}
                  </td>

                  <td style={tdPadding}>
                    {versionData?.dateAvailable && !versionData.dateAvailable.startsWith('0001-01-01')
                      ? moment(versionData.dateAvailable).format('DD MMM YYYY')
                      : '-'}
                  </td>

                  <td
                    style={{ ...tdPadding, display: 'flex', alignItems: 'center', gap: 10, justifyContent: 'center' }}
                    onClick={() => handleDevicePatchClick(item)}
                  >
                    <ProgressBar completed={remaining} total={total} />
                    <span>
                      {remaining}/{total}
                    </span>
                  </td>
                </tr>
              );
            })
          ) : (
            visibleList.map((item: any, i: number) => {
              const disabledBecauseOfDefatom = isDefatomSelected;

              return (
                <tr key={i} style={{ background: i % 2 === 0 ? '#161617' : '#232222' }}>
                  <td style={{ ...tdPadding, textAlign: 'center' }}>
                    <div
                      onClick={() => {
                        if (disabledBecauseOfDefatom) return;
                        handleToggle(item);
                      }}
                      style={{
                        ...getSquareStyle(!!selectedItems[item.KBId]),
                        opacity: disabledBecauseOfDefatom ? 0.3 : 1,
                        cursor: disabledBecauseOfDefatom ? 'not-allowed' : 'pointer',
                      }}
                    />
                  </td>

                  <td style={tdPadding} onClick={() => handleDevicePatchClick(item)}>
                    {item.kbTitle}
                  </td>
                  <td style={tdPadding}>{item.category}</td>

                  <td style={tdPadding}>
                    {item.vulnerabilityDetail?.length
                      ? item.vulnerabilityDetail.map((v: any, idx: number) => {
                          const score = typeof v.score === 'number' ? v.score : Number(v.cvss ?? v.score ?? NaN);
                          return <p key={idx}>{Number.isFinite(score) ? score.toFixed(2) : '--'}</p>;
                        })
                      : '--'}
                  </td>

                  <td style={tdPadding}>
                    {item.vulnerabilityDetail?.length
                      ? item.vulnerabilityDetail.map((v: any, idx: number) => <p key={idx}>{v.cveID || v.cve || 'N/A'}</p>)
                      : '--'}
                  </td>

                  <td style={tdPadding}>
                    {item.dateAvailable && !item.dateAvailable.startsWith('0001-01-01')
                      ? moment(item.dateAvailable).format('DD MMM YYYY')
                      : '-'}
                  </td>

                  <td
                    style={{ ...tdPadding, display: 'flex', alignItems: 'center', gap: 10, justifyContent: 'center' }}
                    onClick={() => handleDevicePatchClick(item)}
                  >
                    {(() => {
                      const tobeinstalled = item.numberOfAgentWithThisKBID ?? 0;
                      const alreadyInstalled = (item.numberOfAgentWithThisKBID ?? 0) + (item.numberOfAgentRequiresThisKBID ?? 0);
                      return (
                        <>
                          <ProgressBar completed={tobeinstalled} total={alreadyInstalled} />
                          <span>
                            {tobeinstalled}/{alreadyInstalled}
                          </span>
                        </>
                      );
                    })()}
                  </td>
                </tr>
              );
            })
          )}
        </tbody>
      </table>

      <Pagination currentPage={currentPage} totalPages={paginationInfo?.totalPages ?? 1} loading={loading} onPageChange={setCurrentPage} />
    </div>
  );
};

export default SoftwareTable;
